import React from 'react';

import Dialog, {DialogProps} from '../../components/Dialog';
import CustomizedDialogTitle from '../../components/Dialog/CustomizedDialogTitle';
import {ModalItem} from '../../stores/ModalsStore';
import useMobileMode from '../../hooks/useMobileMode';

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg';

export interface ModalDialogProps extends Omit<DialogProps, 'title' | 'open'> {
  modal: ModalItem;
  title?: string | JSX.Element | null;
  withoutCloseButton?: boolean;
  open?: boolean;
  onBack?(): void;
  onClose?(): void;
}

export interface ModalDialogRef {
  close: () => void;
}

export const ModalDialog = React.forwardRef<ModalDialogRef, ModalDialogProps>(
  ({modal, title, withoutCloseButton, open, onBack, onClose, ...props}, ref) => {
    const isMobile = useMobileMode();
    const [opened, setOpened] = React.useState<boolean>(true);

    const handleAnimationEnd = () => {
      if (!opened) {
        onClose?.();
        modal.close();
      }
    };

    const handleClose = () => {
      setOpened(false);
    };

    React.useImperativeHandle(ref, () => ({
      close: handleClose,
    }));

    return (
      <Dialog
        open={open || opened}
        closeOnBackdropClick={true}
        onClose={handleClose}
        onDialogTransitionEnd={handleAnimationEnd}
        fullWidth={isMobile}
        animationDirection={isMobile ? 'up' : 'right'}
        {...props}
      >
        {title ? (
          <CustomizedDialogTitle onBack={onBack} onClose={withoutCloseButton ? undefined : handleClose}>
            {title}
          </CustomizedDialogTitle>
        ) : null}
        {props.children}
      </Dialog>
    );
  },
);

ModalDialog.displayName = 'ModalDialog';

export default ModalDialog;
